import React from 'react';
import { AuthorizationError } from '../../types/errors';
import { useAuth } from '../../context/AuthContext';
import { authLogger } from '../../utils/logger';
import { demoService } from '../../services/demoService';

interface Props {
  error: AuthorizationError;
  onDismiss?: () => void;
}

/**
 * Get the display email from account object
 * Handles both MSAL and OIDC account formats
 */
const getAccountEmail = (account: any): string => {
  if (!account) return 'Unknown';
  // MSAL uses 'username', OIDC profile has both 'username' and 'email'
  return account.email || account.username || 'Unknown';
};

/**
 * Modal displayed when user has authorization issues
 * Provides options to switch account or contact support
 */
export const AccountSwitcherModal: React.FC<Props> = ({ error, onDismiss }) => {
  const { account, switchAccount, logout } = useAuth();

  const handleSwitchAccount = async (): Promise<void> => {
    authLogger.info('User initiated account switch from authorization error modal');
    await switchAccount();
  };

  const handleContactSupport = (): void => {
    const supportEmail = generateSupportEmail(error, account);
    window.location.href = supportEmail;
  };

  const handleLogout = async (): Promise<void> => {
    authLogger.info('User initiated logout from authorization error modal');
    await logout();
  };

  const handleBackToLogin = (): void => {
    authLogger.info('User initiated back to login from service unavailable modal');
    // Clear demo mode if active and redirect directly to login
    demoService.deactivateDemoMode(true);
    window.location.href = '/login';
  };

  // Render different UI based on error type
  const renderContent = () => {
    switch (error.type) {
      case 'account_not_registered':
        return (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Account Not Registered
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                You're signed in as:
              </p>
              <div className="bg-gray-100 rounded-md px-4 py-2 mb-4">
                <p className="text-sm font-medium text-gray-900">
                  {error.metadata?.accountEmail || getAccountEmail(account)}
                </p>
              </div>
              <p className="text-sm text-gray-600">
                This account is not registered in the system. Please sign in with an authorized account or contact support for access.
              </p>
            </div>
            <div className="flex flex-col space-y-3">
              <button
                onClick={handleSwitchAccount}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Switch to Different Account
              </button>
              <button
                onClick={handleContactSupport}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Contact Support
              </button>
            </div>
          </>
        );

      case 'account_disabled':
        return (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Account Disabled
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Your account has been deactivated. Please contact support for assistance.
              </p>
              <div className="bg-gray-100 rounded-md px-4 py-2 mb-4">
                <p className="text-sm font-medium text-gray-900">
                  {error.metadata?.accountEmail || getAccountEmail(account)}
                </p>
              </div>
            </div>
            <div className="flex flex-col space-y-3">
              <button
                onClick={handleContactSupport}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Contact Support
              </button>
              <button
                onClick={handleLogout}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Sign Out
              </button>
            </div>
          </>
        );

      case 'insufficient_permissions':
        return (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Insufficient Permissions
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                You don't have access to this feature.
              </p>
              {error.metadata?.requiredRole && (
                <div className="bg-orange-50 border border-orange-200 rounded-md px-4 py-2 mb-4">
                  <p className="text-sm text-orange-800">
                    <span className="font-medium">Required role:</span> {error.metadata.requiredRole}
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-600">
                Try signing in with an account that has the required permissions, or contact your administrator.
              </p>
            </div>
            <div className="flex flex-col space-y-3">
              <button
                onClick={handleSwitchAccount}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Switch to Different Account
              </button>
              <button
                onClick={handleContactSupport}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Contact Administrator
              </button>
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className="w-full inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  Go Back
                </button>
              )}
            </div>
          </>
        );

      case 'resource_access_denied':
        return (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Access Denied
              </h3>
              <p className="text-sm text-gray-600">
                You do not have permission to access this resource.
              </p>
            </div>
            <div className="flex flex-col space-y-3">
              <button
                onClick={handleLogout}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go Back
              </button>
            </div>
          </>
        );

      case 'profile_not_found':
        return (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Welcome! Profile Setup Required
              </h3>
              <p className="text-sm text-gray-600">
                This appears to be your first time signing in. Let's create your profile to get started.
              </p>
            </div>
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => window.location.href = '/onboarding'}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Create Profile
              </button>
              <button
                onClick={handleSwitchAccount}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Use Different Account
              </button>
            </div>
          </>
        );

      case 'service_unavailable':
        return (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Service Unavailable
              </h3>
              <p className="text-sm text-gray-600">
                The service is currently unavailable. This may be due to maintenance or a temporary issue.
              </p>
            </div>
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Try Again
              </button>
              <button
                onClick={handleBackToLogin}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go Back to Login
              </button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true" />

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

/**
 * Generate pre-filled support email with error context
 */
function generateSupportEmail(
  error: AuthorizationError,
  account: any
): string {
  const subject = encodeURIComponent(
    `Authorization Issue - ${error.type.replace(/_/g, ' ')}`
  );

  const body = encodeURIComponent(`
Hello Support Team,

I'm experiencing an authorization issue when trying to access the application:

Account Email: ${error.metadata?.accountEmail || getAccountEmail(account)}
Account ID: ${error.metadata?.accountId || account?.localAccountId || 'Unknown'}
Error Type: ${error.type}
Error Message: ${error.originalMessage}
${error.metadata?.requiredRole ? `Required Role: ${error.metadata.requiredRole}` : ''}
Timestamp: ${new Date().toISOString()}

Please assist with resolving this issue.

Thank you
  `.trim());

  return `mailto:mobilesupport@coint.ai?subject=${subject}&body=${body}`;
}
